import React, { useCallback, useRef, useState, type SetStateAction } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Upload, X, Loader2, GripVertical, AlertTriangle } from 'lucide-react';
import { uploadToStorage } from '../services/supabase';

export interface UploadedImage {
  id: string;
  preview: string;
  url: string | null;
  path: string | null;
  uploading: boolean;
  width?: number;
  height?: number;
}

interface ImageUploaderProps {
  images: UploadedImage[];
  onChange: (value: SetStateAction<UploadedImage[]>) => void;
  userId: string;
  maxImages?: number;
}

function SortableImage({
  image,
  onRemove,
}: {
  image: UploadedImage;
  onRemove: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: image.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="space-y-1.5">
      <div
        className="relative group aspect-[4/3] rounded-lg overflow-hidden border-2 bg-slate-800 border-slate-700"
      >
        <img
          src={image.preview}
          alt=""
          className="w-full h-full object-contain"
        />

        {image.uploading && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <Loader2 className="w-6 h-6 text-white animate-spin" />
          </div>
        )}

        <div className="absolute top-1 left-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="p-1 bg-black/60 rounded text-white cursor-grab active:cursor-grabbing"
          >
            <GripVertical className="w-4 h-4" />
          </button>
        </div>

        <button
          type="button"
          onClick={onRemove}
          className="absolute top-1 right-1 p-1 bg-black/60 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
        >
          <X className="w-4 h-4" />
        </button>

      </div>
    </div>
  );
}

// ------------------------------------
// Image preprocessing: resize + compress before upload
// Prevents fal.ai 422 errors from oversized images
// ------------------------------------
const MAX_DIMENSION = 1920;
const JPEG_QUALITY = 0.85;

function preprocessImage(file: File): Promise<{ file: File; width: number; height: number }> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      const origWidth = width;
      const origHeight = height;

      if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
        const ratio = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) { resolve({ file, width: origWidth, height: origHeight }); return; }
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (!blob) { resolve({ file, width: origWidth, height: origHeight }); return; }
          const name = file.name.replace(/\.\w+$/, '.jpg');
          resolve({ file: new File([blob], name, { type: 'image/jpeg' }), width, height });
        },
        'image/jpeg',
        JPEG_QUALITY
      );
    };
    img.onerror = () => resolve({ file, width: 0, height: 0 });
    img.src = URL.createObjectURL(file);
  });
}

export default function ImageUploader({
  images,
  onChange,
  userId,
  maxImages = 10,
}: ImageUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleFiles = useCallback(
    async (fileList: FileList) => {
      const files = Array.from(fileList).filter((f) =>
        f.type.startsWith('image/')
      );
      const remaining = maxImages - images.length;
      const toUpload = files.slice(0, remaining);

      if (toUpload.length === 0) return;

      const newImages: UploadedImage[] = toUpload.map((file) => ({
        id: crypto.randomUUID(),
        preview: URL.createObjectURL(file),
        url: null,
        path: null,
        uploading: true,
      }));

      const updated = [...images, ...newImages];
      onChange(updated);

      for (let i = 0; i < toUpload.length; i++) {
        try {
          const { file: processed, width, height } = await preprocessImage(toUpload[i]);

          const result = await uploadToStorage(processed, 'images', userId);
          onChange((prev) =>
            prev.map((img) =>
              img.id === newImages[i].id
                ? { ...img, url: result.url, path: result.path, uploading: false, width, height }
                : img
            )
          );
        } catch {
          onChange((prev) =>
            prev.map((img) =>
              img.id === newImages[i].id ? { ...img, uploading: false } : img
            )
          );
        }
      }
    },
    [images, onChange, userId, maxImages]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      if (e.dataTransfer.files.length > 0) {
        handleFiles(e.dataTransfer.files);
      }
    },
    [handleFiles]
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = images.findIndex((img) => img.id === active.id);
      const newIndex = images.findIndex((img) => img.id === over.id);
      onChange(arrayMove(images, oldIndex, newIndex));
    }
  };

  const removeImage = (id: string) => {
    const img = images.find((i) => i.id === id);
    if (img) URL.revokeObjectURL(img.preview);
    const next = images.filter((i) => i.id !== id);
    onChange(next);
  };

  const sizedImages = images.filter((img) => img.width && img.height);
  const hasLandscape = sizedImages.some((img) => (img.width ?? 0) >= (img.height ?? 0));
  const hasPortrait = sizedImages.some((img) => (img.width ?? 0) < (img.height ?? 0));
  const mixedOrientations = hasLandscape && hasPortrait;

  return (
    <div className="space-y-4">
      {/* Drop zone */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-300 ${
          dragOver
            ? 'border-blue-500 bg-blue-900/20 scale-[1.01]'
            : 'border-slate-700 bg-slate-900 hover:border-slate-600'
        }`}
      >
        <Upload className="w-10 h-10 mx-auto text-slate-500 mb-3" />
        <p className="text-sm font-medium text-slate-300">
          Drop images here or click to browse
        </p>
        <p className="text-xs text-slate-500 mt-1">
          JPG, PNG, WebP. Max {maxImages} images. Keep the same orientation so video aspect matches your photos.
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files) handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      {/* Sortable image grid */}
      {images.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={images.map((i) => i.id)}
            strategy={rectSortingStrategy}
          >
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {images.map((image, index) => (
                <div key={image.id} className="relative">
                  <span className="absolute -top-2 -left-2 z-10 w-6 h-6 bg-blue-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {index + 1}
                  </span>
                  <SortableImage
                    image={image}
                    onRemove={() => removeImage(image.id)}
                  />
                </div>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {images.length > 0 && (
        <p className="text-xs text-slate-500">
          Drag to reorder. {images.length}/{maxImages} images.
        </p>
      )}

      {images.length > 0 && sizedImages.length > 0 && !mixedOrientations && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-900/20 p-3">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-amber-300">
              Match video aspect to your images
            </p>
            <p className="text-xs leading-relaxed text-amber-200/80">
              Your photos look {hasPortrait ? 'portrait' : 'landscape'}, so the video will be{' '}
              {hasPortrait ? '9:16 (portrait)' : '16:9 (landscape)'}. Make sure this is the format you want —
              mismatched photos get cropped or letterboxed.
            </p>
          </div>
        </div>
      )}

      {mixedOrientations && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-900/20 p-3">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-amber-300">
              Mixed portrait and landscape images
            </p>
            <p className="text-xs leading-relaxed text-amber-200/80">
              Mixing orientations usually crops or letterboxes poorly in the final video.
              Crop every photo to the same orientation (all landscape or all portrait) before uploading,
              and make sure the video aspect matches your images.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
